// @figma file=tbd node=tbd name="EmptyState" set="tbd" verified=tbd

import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle, Bell, Database, Inbox, SearchX } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

export const emptyStateVariants = cva('flex flex-col items-center justify-center text-center', {
  variants: {
    variant: {
      default: '',
      error: '',
      noResults: '',
      noData: '',
      noNotifications: '',
    },
    size: {
      sm: 'gap-2 py-6',
      default: 'gap-3 py-10',
      lg: 'gap-4 py-16',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

const ICON_MAP: Record<string, React.ElementType> = {
  default: Inbox,
  error: AlertCircle,
  noResults: SearchX,
  noData: Database,
  noNotifications: Bell,
};

const ICON_COLOR_MAP: Record<string, string> = {
  default: 'text-muted-foreground',
  error: 'text-destructive',
  noResults: 'text-muted-foreground',
  noData: 'text-muted-foreground',
  noNotifications: 'text-muted-foreground',
};

const ICON_SIZE_MAP: Record<string, string> = {
  sm: 'h-6 w-6',
  default: 'h-10 w-10',
  lg: 'h-14 w-14',
};

export interface EmptyStateProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof emptyStateVariants> {
  icon?: React.ReactNode;
  heading: string;
  description?: string;
  showCta?: boolean;
  action?: React.ReactNode;
}

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      icon,
      heading,
      description,
      showCta = false,
      action,
      ...props
    },
    ref,
  ) => {
    const variantKey = variant ?? 'default';
    const sizeKey = size ?? 'default';
    const DefaultIcon = ICON_MAP[variantKey] ?? Inbox;
    const iconColorClass = ICON_COLOR_MAP[variantKey] ?? 'text-muted-foreground';
    const iconSizeClass = ICON_SIZE_MAP[sizeKey] ?? 'h-10 w-10';

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        className={cn(emptyStateVariants({ variant, size }), className)}
        {...props}
      >
        <div className={cn(iconColorClass)}>
          {icon ?? <DefaultIcon className={iconSizeClass} aria-hidden="true" />}
        </div>
        <div className="space-y-1">
          <p className="font-semibold text-foreground text-sm">{heading}</p>
          {description && <p className="text-muted-foreground text-sm max-w-xs">{description}</p>}
        </div>
        {showCta && action && <div>{action}</div>}
      </div>
    );
  },
);
EmptyState.displayName = 'EmptyState';

export { EmptyState };
